import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import type FILTERS from '../../common/filters.type';
import customerServices from './customersServices';
import {ADD_CUSTOMER, CUSTOMER_DATA, UPDATE_CUSTOMER} from './customers.types';

const initialState = {
  isLoading: false,
  customers: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as boolean | undefined,
    pageData: [] as CUSTOMER_DATA[],
  },
  customerBattery: [] as {id: number; asset_id: string; name: string}[],
  plans: [] as {
    name: string;
    day: number;
    price: number;
    type: string;
    extra: number;
    swapCount: number;
  }[],
  customerPlan: {} as any,
};

export const customersList = createAsyncThunk(
  'customersList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await customerServices.customersList(payload);
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const customerBattery = createAsyncThunk('customerBattery', async (id: number, thunkAPI) => {
  try {
    const data: AxiosResponse = await customerServices.customerBattery(id);
    return data;
  } catch (error) {
    const result = error as AxiosError;
    return thunkAPI.rejectWithValue(result.message);
  }
});

export const getPlans = createAsyncThunk('getPlans', async (_, thunkAPI) => {
  try {
    const data: AxiosResponse = await customerServices.getPlans();
    return data;
  } catch (error) {
    const result = error as AxiosError;
    return thunkAPI.rejectWithValue(result.message);
  }
});

export const getCustomerPlan = createAsyncThunk('getCustomerPlan', async (id: number, thunkAPI) => {
  try {
    const data: AxiosResponse = await customerServices.getCustomerPlans(id);
    return data;
  } catch (error) {
    const result = error as AxiosError;
    return thunkAPI.rejectWithValue(result.message);
  }
});

export const updateCustomerPlan = createAsyncThunk(
  'updateCustomerPlan',
  async (payload: {customer_id: number; paymentObject: any}, thunkAPI) => {
    try {
      const data: AxiosResponse = await customerServices.updateCustomerPlan(payload);
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addCustomer = createAsyncThunk(
  'addCustomer',
  async (values: ADD_CUSTOMER, thunkAPI) => {
    try {
      const data: AxiosResponse = await customerServices.addCustomer(values);
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const updateCustomer = createAsyncThunk(
  'updateCustomer',
  async (
    values: {
      payload: UPDATE_CUSTOMER;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await customerServices.updateCustomer({
        payload: values.payload,
        id: values.id,
      });
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const customersSlice = createSlice({
  name: 'customers',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.customers.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.customers.page_number = 1;
      state.customers.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.customers.page_number = 1;
      state.customers.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.customers.ascending = action.payload.ascending;
      state.customers.sort_by = action.payload.sortBy;
    },
    customersStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.customers.page_number = 1;
      state.customers.is_active = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(customersList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(customersList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.customers = action.payload.data;
    });
    builder.addCase(customersList.rejected, (state, action) => {
      state.isLoading = false;
      state.customers = initialState.customers;
      message.error(`${action.payload}`);
    });

    builder.addCase(addCustomer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addCustomer.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Customer added successfully`);
    });
    builder.addCase(addCustomer.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateCustomer.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateCustomer.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Customer updated successfully`);
    });
    builder.addCase(updateCustomer.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(customerBattery.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(customerBattery.fulfilled, (state, action) => {
      state.isLoading = false;
      state.customerBattery = action.payload.data;
    });
    builder.addCase(customerBattery.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(getPlans.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getPlans.fulfilled, (state, action) => {
      state.isLoading = false;
      state.plans = action.payload.data.message[0].pricing_model;
    });
    builder.addCase(getPlans.rejected, (state, action) => {
      state.isLoading = false;
      //message.error(`${action.payload}`);
    });

    builder.addCase(getCustomerPlan.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getCustomerPlan.fulfilled, (state, action) => {
      state.isLoading = false;
      state.customerPlan = action.payload.data;
    });
    builder.addCase(getCustomerPlan.rejected, (state, action) => {
      state.isLoading = false;
      //message.error(`${action.payload}`);
    });

    builder.addCase(updateCustomerPlan.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateCustomerPlan.fulfilled, (state, action) => {
      state.isLoading = false;
      message.success(`Paymenr Plan updated successfully`);
    });
    builder.addCase(updateCustomerPlan.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });
  },
});
export const {pageNumberChange, pageSizeChange, searchBy, sortBy, customersStatusFilter} =
  customersSlice.actions;

export default customersSlice.reducer;
